(function () {
  'use strict';

  // ============================================================
  // BRAND CONFIG — the values most likely to change per brand.
  // Jockey NZ: DOM checked live on jockey.co.nz/products/jockey-mens-
  // performance-trunk (2026-09-18) — same selectors and the same four
  // category tables (Men's / Bottoms / Socks / Singlets Size Guide) as
  // Jockey AU, so this file carries over unchanged bar the header note.
  // ============================================================
  const BRAND = {
    fontHeading: '"Montserrat", sans-serif',
    fontWeightHeading: '400',
    colorText: '#001135',
    colorHover: '#230051',
    colorBorder: '#e2e4e8',
    accent: '#001135', // active-tab indicator color
    tabStyle: 'underline', // 'underline' (Jockey-style border) or 'pill' (rounded background chip)
    radius: '999px', // used only when tabStyle is 'pill'
    tabTextTransform: 'none',
    tabLetterSpacing: '0',
  };

  const SEL = {
    openBtn:   '.js-size-chart-open',
    closeBtn:  '.js-size-chart-close',
    container: '.js-size-chart-container',
    tableWrap: '.rte__table-wrapper',
    table:     '.size-table',
  };

  // Category label (as it appears in the <h4> title row of each table) ->
  // { slug, keywords to match against the product title/breadcrumb for auto-selecting this tab }
  // This is the per-catalogue piece most likely to need editing per brand —
  // update the categories and keywords to match that brand's own range.
  const CATEGORY_RULES = [
    { match: /bottoms/i,  slug: 'bottoms',  keywords: [/trunk/i, /brief/i, /boxer/i, /short/i] },
    { match: /socks?/i,   slug: 'socks',    keywords: [/sock/i] },
    { match: /singlet/i,  slug: 'singlets', keywords: [/singlet/i, /tank/i] },
    { match: /.*/,        slug: 'general',  keywords: [] }, // fallback / catch-all "Men's Size Guide"
  ];

  let built = false;

  function log(...args) { console.log('[size-guide-tabs]', ...args); }

  function guessDefaultSlug() {
    const haystack = [
      document.querySelector('h1')?.textContent || '',
      document.title || '',
      location.pathname || '',
    ].join(' ');

    for (const rule of CATEGORY_RULES) {
      if (rule.slug === 'general') continue;
      if (rule.keywords.some((re) => re.test(haystack))) return rule.slug;
    }
    return 'bottoms'; // most Jockey PDPs are underwear bottoms; adjust per-catalogue if needed
  }

  function slugFor(titleText) {
    const rule = CATEGORY_RULES.find((r) => r.match.test(titleText));
    return rule ? rule.slug : 'general';
  }

  function buildTabs(container) {
    if (built) return;

    const wraps = Array.from(container.querySelectorAll(SEL.tableWrap));
    if (!wraps.length) return; // tables not rendered yet, try again later

    const entries = wraps.map((wrap) => {
      const table = wrap.querySelector(SEL.table);
      const titleEl = table?.querySelector('th h4, td h4, h4');
      const title = (titleEl?.textContent || 'Size Guide').trim();
      const slug = slugFor(title);
      // Drop the redundant title row now that it lives in the tab label.
      titleEl?.closest('tr')?.remove();
      return { wrap, title, slug };
    });

    const nav = document.createElement('div');
    nav.className = 'sgt-tabs';
    nav.setAttribute('role', 'tablist');
    nav.innerHTML = entries
      .map(
        (e) =>
          `<button type="button" class="sgt-tab" role="tab" data-slug="${e.slug}" aria-selected="false">${shortLabel(e.title)}</button>`
      )
      .join('');

    const firstWrap = entries[0].wrap;
    firstWrap.parentElement.insertBefore(nav, firstWrap);

    entries.forEach((e) => e.wrap.classList.add('sgt-panel'));

    nav.addEventListener('click', (ev) => {
      const btn = ev.target.closest('.sgt-tab');
      if (!btn) return;
      activate(btn.dataset.slug);
    });

    injectStyles();
    built = true;
    container.__sgtEntries = entries;
    container.__sgtNav = nav;
    log('tabs built:', entries.map((e) => e.slug));
  }

  function shortLabel(title) {
    return title
      .replace(/^Men'?s\s*/i, '')
      .replace(/Size Guide$/i, '')
      .trim() || 'General';
  }

  function activate(slug) {
    const container = document.querySelector(SEL.container);
    if (!container || !container.__sgtEntries) return;
    const entries = container.__sgtEntries;
    const match = entries.find((e) => e.slug === slug) || entries.find((e) => e.slug === 'general') || entries[0];

    entries.forEach((e) => {
      const isActive = e === match;
      e.wrap.style.display = isActive ? '' : 'none';
    });
    container.__sgtNav.querySelectorAll('.sgt-tab').forEach((btn) => {
      btn.setAttribute('aria-selected', btn.dataset.slug === match.slug ? 'true' : 'false');
    });
  }

  function openModal() {
    const container = document.querySelector(SEL.container);
    if (!container) return;
    buildTabs(container);
    document.body.classList.add('size-chart-is-open'); // fixes the currently-broken trigger
    activate(guessDefaultSlug());
    log('opened, default tab:', guessDefaultSlug());
  }

  function closeModal() {
    document.body.classList.remove('size-chart-is-open');
  }

  function injectStyles() {
    // Two visual treatments for the active tab, selected by BRAND.tabStyle:
    // 'underline' (thin border under the label, Jockey/Russell style) or
    // 'pill' (rounded background chip, Mitch Dowd's signature).
    const activeTabCss =
      BRAND.tabStyle === 'pill'
        ? `
      .sgt-tab { border-radius: ${BRAND.radius}; padding: 6px 16px; border-bottom: none; }
      .sgt-tab[aria-selected="true"] { color: #fff; background: ${BRAND.accent}; }
    `
        : `
      .sgt-tab { border-bottom: 2px solid transparent; padding: 0 0 10px; }
      .sgt-tab[aria-selected="true"] { color: ${BRAND.colorText}; border-bottom-color: ${BRAND.accent}; }
    `;

    const style = document.createElement('style');
    style.textContent = `
      .sgt-tabs {
        display: flex;
        gap: ${BRAND.tabStyle === 'pill' ? '10px' : '20px'};
        margin: 4px 0 20px;
        ${BRAND.tabStyle === 'pill' ? '' : `border-bottom: 1px solid ${BRAND.colorBorder};`}
        overflow-x: auto;
      }
      .sgt-tab {
        appearance: none;
        background: none;
        border: none;
        font: inherit;
        font-family: ${BRAND.fontHeading};
        font-weight: ${BRAND.fontWeightHeading};
        font-size: 13px;
        letter-spacing: ${BRAND.tabLetterSpacing};
        text-transform: ${BRAND.tabTextTransform};
        color: ${BRAND.colorText};
        cursor: pointer;
        white-space: nowrap;
      }
      .sgt-tab:hover { color: ${BRAND.colorHover}; }
      ${activeTabCss}
      .sgt-panel table.size-table { margin-top: 0; }
    `;
    document.head.appendChild(style);
  }

  // Event delegation: works regardless of when the modal markup renders,
  // and regardless of whether the theme's own (currently broken) handler fires.
  document.addEventListener('click', (e) => {
    if (e.target.closest(SEL.openBtn)) openModal();
    if (e.target.closest(SEL.closeBtn)) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeModal();
  });

  log('loaded');
})();
